/**
 * HelpCenterPage - Comprehensive Help Center with sidebar navigation
 * 
 * Features:
 * - Role-based content filtering
 * - Sidebar navigation (Windows Help style)
 * - Full-text search
 * - Clickable in-app links
 * - Ask Audia integration
 * - Mobile responsive
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ReaderNav from '../components/ReaderNav';
import CreatorNav from '../components/CreatorNav';
import PublisherNav from '../components/PublisherNav';
import ReaderMobileBottomNav from '../components/ReaderMobileBottomNav';
import UniversalFooter from '../components/UniversalFooter';
import AskAudiaHelpButton from '../components/AskAudiaHelpButton';
import { Button } from '../components/ui/button';
import { 
  Search, 
  ChevronRight, 
  ChevronDown,
  Rocket,
  Settings,
  Library,
  Headphones,
  Coins,
  PenTool,
  BookUp,
  Building2,
  LifeBuoy,
  ExternalLink,
  ArrowLeft,
  Menu,
  X,
  Home
} from 'lucide-react';
import { 
  helpCenterContent, 
  getSectionsForRole, 
  getArticle, 
  searchArticles 
} from '../data/helpCenterContent';

// Icon mapping
const iconMap = {
  Rocket: Rocket,
  Settings: Settings,
  Search: Search,
  Library: Library,
  Headphones: Headphones,
  Coins: Coins,
  PenTool: PenTool,
  BookUp: BookUp,
  Building2: Building2,
  LifeBuoy: LifeBuoy
};

const HelpCenterPage = () => {
  const { sectionId, articleId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  // Determine user's primary role
  const userRole = useMemo(() => {
    if (!user?.roles) return 'reader';
    if (user.roles.includes('publisher')) return 'publisher';
    if (user.roles.includes('author') || user.roles.includes('creator')) return 'author';
    return 'reader';
  }, [user]);

  // Get home route based on user role
  const getHomeRoute = () => {
    switch (userRole) {
      case 'publisher':
        return '/publisher';
      case 'author':
        return '/creator';
      default:
        return '/home';
    }
  };

  // Get sections for user's role
  const sections = useMemo(() => getSectionsForRole(userRole), [userRole]);
  
  // State
  const [expandedSections, setExpandedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Current article
  const currentArticle = useMemo(() => {
    if (sectionId && articleId) {
      return getArticle(sectionId, articleId);
    }
    return null;
  }, [sectionId, articleId]);

  // Current section
  const currentSection = useMemo(() => {
    return sections.find(s => s.id === sectionId) || null;
  }, [sections, sectionId]);

  // Auto-expand current section - using useMemo to derive state
  const derivedExpandedSections = useMemo(() => {
    if (sectionId) {
      return { ...expandedSections, [sectionId]: true };
    }
    return expandedSections;
  }, [sectionId, expandedSections]);

  // Handle search - derive results directly
  const { isSearching, searchResults } = useMemo(() => {
    if (searchQuery.trim().length >= 2) {
      const results = searchArticles(searchQuery, userRole);
      return { isSearching: true, searchResults: results };
    }
    return { isSearching: false, searchResults: [] };
  }, [searchQuery, userRole]);

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Navigate to article
  const goToArticle = (sectionId, articleId) => {
    navigate(`/help/${sectionId}/${articleId}`);
    setMobileSidebarOpen(false);
    setSearchQuery('');
  };

  // Render navigation based on role
  const renderNav = () => {
    switch (userRole) {
      case 'publisher':
        return <PublisherNav />;
      case 'author':
        return <CreatorNav />;
      default:
        return <ReaderNav />;
    }
  };

  // Render markdown-like content
  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    const elements = [];
    let currentList = [];
    let inTable = false;
    let tableRows = [];

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-1 mb-4 text-slate-300">
            {currentList.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        );
        currentList = [];
      }
    };

    const flushTable = () => {
      if (tableRows.length > 0) {
        elements.push(
          <div key={`table-${elements.length}`} className="overflow-x-auto mb-4">
            <table className="w-full text-sm border border-slate-700 rounded-lg overflow-hidden">
              <thead className="bg-slate-800">
                <tr>
                  {tableRows[0].map((cell, i) => (
                    <th key={i} className="px-4 py-2 text-left text-slate-200 font-semibold border-b border-slate-700">
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.slice(2).map((row, ri) => (
                  <tr key={ri} className="border-b border-slate-800 last:border-0">
                    {row.map((cell, ci) => {
                      const formatted = cell.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>');
                      return (
                        <td key={ci} className="px-4 py-2 text-slate-300" dangerouslySetInnerHTML={{ __html: formatted }} />
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        tableRows = [];
        inTable = false;
      }
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      // Table detection
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        flushList();
        inTable = true;
        const cells = trimmed.split('|').filter(c => c.trim()).map(c => c.trim());
        if (!trimmed.includes('---')) {
          tableRows.push(cells);
        }
        return;
      } else if (inTable) {
        flushTable();
      }

      // Headers
      if (trimmed.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={index} className="text-xl font-bold text-white mt-6 mb-3">
            {trimmed.replace('## ', '')}
          </h2>
        );
      } else if (trimmed.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={index} className="text-lg font-semibold text-white mt-5 mb-2">
            {trimmed.replace('### ', '')}
          </h3>
        );
      }
      // List items
      else if (trimmed.startsWith('- ')) {
        const text = trimmed.replace('- ', '');
        // Handle bold text
        const formatted = text.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>');
        currentList.push(<span dangerouslySetInnerHTML={{ __html: formatted }} />);
      }
      // Numbered list
      else if (/^\d+\.\s/.test(trimmed)) {
        flushList();
        const text = trimmed.replace(/^\d+\.\s/, '');
        const formatted = text.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>');
        elements.push(
          <p key={index} className="mb-2 text-slate-300 pl-4">
            <span className="text-purple-400 font-semibold mr-2">{trimmed.match(/^\d+/)[0]}.</span>
            <span dangerouslySetInnerHTML={{ __html: formatted }} />
          </p>
        );
      }
      // Regular paragraph
      else if (trimmed) {
        flushList();
        const formatted = trimmed.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>');
        elements.push(
          <p key={index} className="mb-3 text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatted }} />
        );
      }
    });

    flushList();
    flushTable();

    return elements;
  };

  // Render related links
  const renderRelatedLinks = (links) => {
    if (!links || links.length === 0) return null;

    return (
      <div className="mt-8 pt-6 border-t border-slate-700">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
          Related Links
        </h3>
        <div className="flex flex-wrap gap-2">
          {links.map((link, index) => {
            const isAppLink = link.type === 'app';
            const isHelpLink = link.type === 'help';

            if (isAppLink) {
              return (
                <Link
                  key={index}
                  to={link.path}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 hover:text-purple-200 rounded-lg text-sm transition-colors"
                >
                  {link.text}
                  <ExternalLink className="w-3 h-3" />
                </Link>
              );
            } else if (isHelpLink) {
              return (
                <button
                  key={index}
                  onClick={() => navigate(link.path)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-700/50 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg text-sm transition-colors"
                >
                  {link.text}
                  <ChevronRight className="w-3 h-3" />
                </button>
              );
            }
            return null;
          })}
        </div>
      </div>
    );
  };

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Help Center</h1>
          <p className="text-slate-400 mb-6">Please sign in to access the Help Center.</p>
          <Link to="/login" className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Navigation */}
      {renderNav()}

      {/* Header / Hero */}
      <header className="bg-gradient-to-br from-indigo-600 via-purple-600 to-sky-500">
        <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-indigo-100 mb-2">Help Center</p>
              <h1 className="text-3xl sm:text-4xl font-bold">How can we help you?</h1>
              <p className="mt-2 text-indigo-100/90 max-w-xl">
                Browse our comprehensive guides or search for specific topics.
                {"Can't"} find what you need? Ask Audia for instant assistance.
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-6 max-w-2xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search help articles..."
                className="w-full pl-12 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30"
                data-testid="help-search-input"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Ask Audia Section - Below Hero */}
      <section className="bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-purple-500/25">
                <span className="text-xl">🎧</span>
              </div>
              <div>
                <p className="text-white font-semibold">Hi, I'm Audia. How can I help you today?</p>
                <p className="text-slate-400 text-sm">Get instant answers to your questions</p>
              </div>
            </div>
            <AskAudiaHelpButton 
              context="help-center" 
              className="w-full sm:w-auto"
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Search Results */}
        {isSearching && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4">
              Search Results for &ldquo;{searchQuery}&rdquo; ({searchResults.length} found)
            </h2>
            {searchResults.length > 0 ? (
              <div className="space-y-3">
                {searchResults.map((result, index) => (
                  <button
                    key={index}
                    onClick={() => goToArticle(result.sectionId, result.articleId)}
                    className="w-full text-left p-4 bg-slate-900/60 hover:bg-slate-800/80 border border-slate-800 rounded-xl transition-colors"
                  >
                    <div className="flex items-center gap-2 text-purple-400 text-sm mb-1">
                      <span>{result.sectionTitle}</span>
                      <ChevronRight className="w-3 h-3" />
                    </div>
                    <h3 className="text-white font-medium">{result.articleTitle}</h3>
                    <p className="text-slate-400 text-sm mt-1 line-clamp-2">{result.excerpt}</p>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-slate-400">No articles found. Try different keywords or ask Audia for help.</p>
            )}
          </div>
        )}

        {/* Content Grid */}
        {!isSearching && (
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
            {/* Mobile Sidebar Toggle */}
            <button
              onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
              className="lg:hidden flex items-center gap-2 px-4 py-2 bg-slate-800 rounded-lg text-white mb-4"
            >
              {mobileSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              {mobileSidebarOpen ? 'Close Menu' : 'Browse Topics'}
            </button>

            {/* Sidebar Navigation */}
            <nav className={`lg:block ${mobileSidebarOpen ? 'block' : 'hidden'}`}>
              <div className="sticky top-4 bg-slate-900/60 border border-slate-800 rounded-2xl p-4 space-y-2">
                <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider px-2 mb-3">
                  Topics
                </h2>
                {sections.map((section) => {
                  const IconComponent = iconMap[section.icon] || Rocket;
                  const isExpanded = derivedExpandedSections[section.id];
                  const isActive = currentSection?.id === section.id;

                  return (
                    <div key={section.id}>
                      {/* Section Header */}
                      <button
                        onClick={() => toggleSection(section.id)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                          isActive 
                            ? 'bg-purple-600/20 text-purple-300' 
                            : 'hover:bg-slate-800 text-slate-300'
                        }`}
                        data-testid={`help-section-${section.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <IconComponent className="w-4 h-4" />
                          <span className="text-sm font-medium">{section.title}</span>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>

                      {/* Section Articles */}
                      {isExpanded && (
                        <div className="ml-6 mt-1 space-y-1">
                          {section.articles.map((article) => {
                            const isArticleActive = currentArticle?.id === article.id;
                            return (
                              <button
                                key={article.id}
                                onClick={() => goToArticle(section.id, article.id)}
                                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors ${
                                  isArticleActive
                                    ? 'bg-purple-600/30 text-white'
                                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                                }`}
                                data-testid={`help-article-${article.id}`}
                              >
                                {article.title}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </nav>

            {/* Article Content */}
            <div className="min-h-[60vh]">
              {currentArticle ? (
                <article className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8">
                  {/* Breadcrumb */}
                  <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
                    <button
                      onClick={() => navigate('/help')}
                      className="hover:text-white transition-colors"
                    >
                      Help Center
                    </button>
                    <ChevronRight className="w-4 h-4" />
                    <span className="text-purple-400">{currentSection?.title}</span>
                    <ChevronRight className="w-4 h-4" />
                    <span className="text-white">{currentArticle.title}</span>
                  </div>

                  {/* Article Title */}
                  <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6">
                    {currentArticle.title}
                  </h1>

                  {/* Article Content */}
                  <div className="prose prose-invert max-w-none">
                    {renderContent(currentArticle.content)}
                  </div>

                  {/* Related Links */}
                  {renderRelatedLinks(currentArticle.relatedLinks)}

                  {/* Navigation */}
                  <div className="mt-8 pt-6 border-t border-slate-700 flex items-center justify-between">
                    <button
                      onClick={() => navigate('/help')}
                      className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to Help Center
                    </button>
                  </div>
                </article>
              ) : (
                /* Landing - All Sections Overview */
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-white mb-2">Browse by Topic</h2>
                    <p className="text-slate-400">Select a category to explore our help articles</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {sections.map((section) => {
                      const IconComponent = iconMap[section.icon] || Rocket;
                      return (
                        <button
                          key={section.id}
                          onClick={() => {
                            toggleSection(section.id);
                            if (section.articles.length > 0) {
                              goToArticle(section.id, section.articles[0].id);
                            }
                          }}
                          className="text-left p-5 bg-slate-900/60 hover:bg-slate-800/80 border border-slate-800 hover:border-purple-500/30 rounded-2xl transition-all group"
                          data-testid={`help-card-${section.id}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-purple-600/20 rounded-xl group-hover:bg-purple-600/30 transition-colors">
                              <IconComponent className="w-6 h-6 text-purple-400" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-white group-hover:text-purple-300 transition-colors">
                                {section.title}
                              </h3>
                              <p className="text-sm text-slate-400 mt-1">
                                {section.articles.length} article{section.articles.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400 transition-colors" />
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Quick Help CTA */}
                  <div className="mt-8 p-6 bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border border-purple-500/30 rounded-2xl text-center">
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {"Can't"} find what {"you're"} looking for?
                    </h3>
                    <p className="text-slate-300 mb-4">
                      Ask Audia for instant help or contact our support team.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <AskAudiaHelpButton context="help-center" />
                      <Link
                        to="/contact"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors"
                      >
                        Contact Support
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>

                  {/* Return Home Button */}
                  <div className="mt-8 flex justify-center">
                    <Link
                      to={getHomeRoute()}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-colors border border-slate-700 hover:border-slate-600"
                      data-testid="help-return-home-btn"
                    >
                      <Home className="w-5 h-5" />
                      Return Home
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <UniversalFooter />
      <ReaderMobileBottomNav />
    </div>
  );
};

export default HelpCenterPage;
